import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, geometry, outlined, taper } from '../effect-guns.sprite';

/**
 * Main Event (the Luchador set's gun, ADR-0160 amended): a championship belt made a pistol. A black leather body (lit on
 * top, its back end rounded), a big gold plate on its side with a ruby in its middle, a brass ring bell on top at its front with
 * a striker over it, a black barrel studded in gold and a gold muzzle ring. On a shot (`fire`) the striker rings the
 * bell (lines ringing off it), the ruby flashes and the muzzle flashes white, then gold.
 */

const LEATHER = ['k', '1', '2', '3'] as const;

/** The plate's oval, and the ruby in its middle. */
const PLATE = [28, 20.5, 8.5, 5.6] as const;
const RUBY = [28, 20.5, 2.6, 2] as const;
const inOval = (x: number, y: number, [cx, cy, rx, ry]: readonly number[]) =>
  ((x + 0.5 - (cx as number)) / (rx as number)) ** 2 + ((y + 0.5 - (cy as number)) / (ry as number)) ** 2;

const belt = (glint: number, heat = 0, ring = 0) =>
  outlined((c: PixelCanvas) => {
    // The body: black leather, lit on top, its back rounded off, gold studs along its top edge.
    box(c, 3, 15, 38, 26, [3, 1], byRow(15, 26, LEATHER, 1, 2), 'l');
    for (let x = 6; x <= 18; x += 4) c.set(x, 16, 'Y');
    // The gold plate: lit on its top-left, its rim engraved, a glint crossing it.
    for (let y = 14; y <= 27; y++)
      for (let x = 19; x <= 37; x++) {
        const e = inOval(x, y, PLATE);
        if (e > 1) continue;
        const r = inOval(x, y, RUBY);
        if (r < 1) c.set(x, y, heat ? 'w' : x < 28 && y < 20 ? 'i' : x + y > 50 ? 'R' : 'r');
        else if (r < 1.8) c.set(x, y, 'w');
        else if (Math.abs(x - glint) < 1 && e < 0.8) c.set(x, y, 'w');
        else if (e > 0.7) c.set(x, y, (x * 3 + y) % 4 === 0 ? 'y' : x + y > 51 ? 'U' : 'Y');
        else c.set(x, y, x + y < 45 ? 'y' : x + y > 53 ? 'U' : 'Y');
      }
    // The ring bell on its back: a brass dome on a post, lit on its left, a knob on top.
    box(c, 41, 13, 42, 15, [], (): PaletteKey => '3');
    for (let y = 7; y <= 12; y++) {
      const half = 1 + (y - 7) * 0.6;
      for (let x = Math.round(41.5 - half); x <= Math.round(41.5 + half); x++)
        c.set(x, y, y === 12 ? 'U' : x < 41 ? 'y' : x > 42 ? 'U' : 'Y');
    }
    c.set(41, 6, 'Y');
    c.set(42, 6, 'U');
    // The striker: raised over the bell, or down on it as it fires.
    if (ring) {
      c.hline(45, 49, 10, '3');
      c.set(44, 10, '5');
      c.set(44, 9, '5');
    } else {
      c.vline(48, 7, 15, '3');
      c.set(47, 6, '5');
      c.set(48, 6, '5');
    }
    // Lines ringing off the bell.
    if (ring >= 2) {
      c.set(37, 6, 'w');
      c.set(36, 8, 'w');
      c.set(45, 5, 'w');
      c.set(46, 4, 'w');
      c.set(37, 4, 'w');
    } else if (ring === 1) {
      c.set(36, 5, 'y');
      c.set(35, 7, 'y');
      c.set(46, 3, 'y');
    }
    // The trigger under it.
    box(c, 24, 27, 27, 29, [0, 1], (_x, y): PaletteKey => (y === 27 ? 'Y' : 'U'), 'r');
    // The barrel: black leather, studded in gold down its middle, a glint along its top.
    taper(c, 39, 58, 20, 3, 2, (top, bottom, x): PaletteKey => {
      if (top === 0) return Math.abs(x - glint * 2) < 2 ? 'w' : '3';
      if (bottom === 0) return 'k';
      if (top === 2 && x % 5 === 1) return x % 10 === 1 ? 'y' : 'Y';
      return top === 1 ? '2' : '1';
    });
    // The gold muzzle ring, flashing white, then gold, as it fires.
    taper(c, 59, 63, 20, 3, 2.5, (top, bottom, x): PaletteKey => {
      if (heat === 2) return 'w';
      if (heat === 1) return top === 0 ? 'w' : 'y';
      if (x === 63) return 'U';
      return top === 0 ? 'y' : bottom === 0 ? 'U' : 'Y';
    });
  });

export default sprite({
  name: 'pollinator_luchador',
  ...geometry,
  notes: `Main Event (ADR-0160 amended, the Luchador set's gun): a championship belt made a pistol. A black leather body
(lit on top, gold studs along it, its back end rounded), a big gold plate on its side (lit top-left, its rim engraved, a
ruby in a white ring in its middle, a glint crossing it over 4 frames), a brass ring bell on a post on top at its front with a
striker raised over it, a gold trigger, a black barrel studded in gold, a gold muzzle ring. Fire: the striker comes
down on the bell, lines ring off it white then gold, the ruby and the muzzle flash white, then gold, over 4 frames. Its
flair: the ruby's glow, a glint. No team keys. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
  anims: {
    idle: { fps: 8, frames: [belt(22), belt(26), belt(30), belt(34)] },
    fire: { fps: 20, loop: false, frames: [belt(0, 2, 2), belt(0, 1, 2), belt(0, 0, 1), belt(0)] },
  },
});
